import { api } from '../lib/api.js';

/**
 * THE FLAGGED-SCAN QUEUE, PER SCAN — the client half of
 * `packages/api/src/services/flaggedScans.service.ts`, whose header carries the
 * reasoning. In short: the console's older review surface reads
 * `/attendance/live`, which returns each employee's LATEST event, so it can
 * only ever show one flagged punch per person and cannot clear a day.
 */

export interface FlaggedScan {
  id: string;
  type: string;
  /** UTC instant. Format it; never truncate it to a day (docs/time-and-dates.md). */
  at: string;
  status: string;
  flaggedReason: string | null;
  source: string;
  worksite: { id: string; name: string } | null;
  faceMatchConfidence: number | null;
  photoUrl: string | null;
  /** Already reaching the day totals provisionally — see the service. */
  countsTowardRollup: boolean;
}

export interface FlaggedScanGroup {
  key: string;
  employeeId: string;
  employeeCode: string;
  employeeName: string;
  /** `YYYY-MM-DD` cut in the COMPANY zone by the server, never by the browser. */
  dayKey: string;
  scans: FlaggedScan[];
}

export interface FlaggedScanPage {
  groups: FlaggedScanGroup[];
  scanCount: number;
  hasMore: boolean;
  timezone: string;
}

/** The queue's own key, and the badge's — the badge counts what the page lists. */
export const FLAGGED_SCANS_QUERY_KEY = ['flagged-scans'] as const;
export const FLAGGED_SCAN_COUNT_QUERY_KEY = ['flagged-scans', 'count'] as const;

export const flaggedScansApi = {
  async list(params: { worksiteId?: string; limit?: number } = {}): Promise<FlaggedScanPage> {
    const body = await api.get<{ data: FlaggedScanPage }>('/attendance/events/flagged', {
      params,
    });
    return body.data;
  },

  async count(): Promise<number> {
    const body = await api.get<{ data: { count: number } }>('/attendance/events/flagged/count');
    return body.data.count;
  },

  /**
   * One press, one act. The server writes every status before it rebuilds any
   * day, so a day is never derived from a half-decided punch stream — which is
   * why the page sends a set rather than looping the single-event endpoint.
   */
  async decide(payload: {
    eventIds: string[];
    status: 'VALID' | 'REJECTED';
    reason: string;
  }): Promise<{ decided: number; skipped: string[] }> {
    const body = await api.patch<{ data: { decided: number; skipped: string[] } }>(
      '/attendance/events/status',
      payload
    );
    return body.data;
  },
};
